import { defineStore } from 'pinia';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import DealsAPI from 'dashboard/api/deals';
import LossReasonsAPI from 'dashboard/api/lossReasons';

const camelize = value => camelcaseKeys(value || {}, { deep: true });

export const useDealsStore = defineStore('deals', {
  state: () => ({
    deals: [],
    stages: [],
    companies: [],
    agents: [],
    lossReasons: [],
    isLoading: false,
    isSaving: false,
  }),

  actions: {
    replaceDeal(deal) {
      const normalized = camelize(deal);
      const index = this.deals.findIndex(item => item.id === normalized.id);
      if (index === -1) this.deals.push(normalized);
      else this.deals[index] = normalized;
      return normalized;
    },

    async fetchPipeline(filters = {}) {
      this.isLoading = true;
      try {
        const { data } = await DealsAPI.pipeline(snakecaseKeys(filters));
        const payload = camelize(data.payload);
        this.deals = payload.deals;
        this.stages = payload.stages;
        this.companies = payload.companies;
        this.agents = payload.agents;
        this.lossReasons = payload.lossReasons;
      } finally {
        this.isLoading = false;
      }
    },

    async fetchPipelineConfiguration() {
      const { data } = await DealsAPI.pipelineConfiguration();
      this.stages = camelize(data.payload).stages;
      return this.stages;
    },

    async updatePipelineConfiguration(stages) {
      const { data } = await DealsAPI.updatePipelineConfiguration(
        snakecaseKeys(stages, { deep: true })
      );
      this.stages = camelize(data.payload).stages;
      return this.stages;
    },

    async createDeal(attributes) {
      this.isSaving = true;
      try {
        const { data } = await DealsAPI.create({
          deal: snakecaseKeys(attributes),
        });
        return this.replaceDeal(data.payload);
      } finally {
        this.isSaving = false;
      }
    },

    async moveDeal(id, stageId, lostReasonId) {
      const { data } = await DealsAPI.move(id, { stageId, lostReasonId });
      return this.replaceDeal(data.payload);
    },

    async findSimilar(companyId, name) {
      if (!companyId || name.trim().length < 3) return [];
      const { data } = await DealsAPI.similar({ companyId, name });
      return camelize(data.payload);
    },

    async createLossReason(name) {
      const { data } = await LossReasonsAPI.create({ loss_reason: { name } });
      const reason = camelize(data.payload);
      this.lossReasons.push(reason);
      return reason;
    },

    async showDeal(id) {
      const { data } = await DealsAPI.show(id);
      return this.replaceDeal(data.payload);
    },

    async refreshDeal(id) {
      const { data } = await DealsAPI.show(id);
      return this.replaceDeal(data.payload);
    },

    async updateDeal(id, attributes) {
      const { data } = await DealsAPI.updateDeal(
        id,
        snakecaseKeys(attributes, { deep: true })
      );
      return this.replaceDeal(data.payload);
    },

    async markWon(id) {
      const { data } = await DealsAPI.won(id);
      return this.replaceDeal(data.payload);
    },

    async markLost(id, lostReasonId) {
      const { data } = await DealsAPI.lost(id, lostReasonId);
      return this.replaceDeal(data.payload);
    },

    async reactivate(id) {
      const { data } = await DealsAPI.reactivate(id);
      return this.replaceDeal(data.payload);
    },

    async createActivity(id, attributes) {
      await DealsAPI.createActivity(
        id,
        snakecaseKeys(attributes, { deep: true })
      );
      return this.refreshDeal(id);
    },

    async updateActivity(id, activityId, attributes) {
      await DealsAPI.updateActivity(
        id,
        activityId,
        snakecaseKeys(attributes, { deep: true })
      );
      return this.refreshDeal(id);
    },

    async createComment(id, body) {
      await DealsAPI.createComment(id, body);
      return this.refreshDeal(id);
    },

    async createDocument(id, attributes, file) {
      await DealsAPI.createDocument(id, snakecaseKeys(attributes), file);
      return this.refreshDeal(id);
    },
  },
});
